/**
 * DRAFT - Compliance Frameworks Display Component
 * Showcases 40+ industry compliance frameworks with categorization
 * Used in: Landing Page, Phase 2 Audit Results, Phase 1 Form
 */

import { useState, memo } from 'react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Shield, Building2, Heart, Landmark, Lock, Globe, Cloud, 
  Code, Factory, CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronUp,
  Award, FileCheck, Zap, Activity
} from "lucide-react";
import { cn } from "@/lib/utils";

// Complete framework data organized by category
export const COMPLIANCE_FRAMEWORKS = {
  security: {
    name: "Security & Privacy",
    icon: Shield,
    color: "text-red-400",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/30",
    frameworks: [
      { id: "NIST_800_53", name: "NIST 800-53", desc: "Security Controls for Federal Systems", tier: "enterprise" },
      { id: "NIST_CSF", name: "NIST CSF 2.0", desc: "Cybersecurity Framework", tier: "growth" },
      { id: "ISO_27001", name: "ISO 27001", desc: "Information Security Management", tier: "growth" },
      { id: "ISO_27017", name: "ISO 27017", desc: "Cloud Security Controls", tier: "enterprise" },
      { id: "ISO_27018", name: "ISO 27018", desc: "PII Protection in Cloud", tier: "enterprise" },
      { id: "CIS_CONTROLS", name: "CIS Controls v8", desc: "Critical Security Controls", tier: "growth" },
      { id: "CIS_BENCHMARKS", name: "CIS Benchmarks", desc: "Cloud Provider Benchmarks", tier: "growth" },
    ]
  },
  audit: {
    name: "Audit & Compliance",
    icon: FileCheck,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    frameworks: [
      { id: "SOC_2", name: "SOC 2 Type II", desc: "Trust Services Criteria", tier: "growth" },
      { id: "SOC_1", name: "SOC 1 Type II", desc: "Financial Reporting Controls", tier: "enterprise" },
      { id: "SOX", name: "SOX", desc: "Sarbanes-Oxley Act", tier: "enterprise" },
    ]
  },
  healthcare: {
    name: "Healthcare",
    icon: Heart,
    color: "text-pink-400",
    bgColor: "bg-pink-500/10",
    borderColor: "border-pink-500/30",
    frameworks: [
      { id: "HIPAA", name: "HIPAA", desc: "Health Information Privacy", tier: "growth" },
      { id: "HITRUST_CSF", name: "HITRUST CSF", desc: "Healthcare Security Framework", tier: "enterprise" },
      { id: "HITECH", name: "HITECH", desc: "Health IT Standards", tier: "enterprise" },
    ]
  },
  government: {
    name: "Government & Federal",
    icon: Landmark,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    frameworks: [
      { id: "FEDRAMP", name: "FedRAMP", desc: "Federal Cloud Authorization", tier: "enterprise" },
      { id: "FISMA", name: "FISMA", desc: "Federal Security Management", tier: "enterprise" },
      { id: "CMMC", name: "CMMC 2.0", desc: "DoD Cybersecurity Maturity", tier: "enterprise" },
      { id: "NIST_800_171", name: "NIST 800-171", desc: "CUI Protection", tier: "enterprise" },
      { id: "ITAR", name: "ITAR", desc: "Arms Regulations", tier: "enterprise" },
      { id: "CJIS", name: "CJIS", desc: "Criminal Justice Security", tier: "enterprise" },
    ]
  },
  privacy: {
    name: "Data Privacy",
    icon: Lock,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    frameworks: [
      { id: "GDPR", name: "GDPR", desc: "EU Data Protection", tier: "growth" },
      { id: "CCPA", name: "CCPA/CPRA", desc: "California Privacy", tier: "growth" },
      { id: "LGPD", name: "LGPD", desc: "Brazil Data Protection", tier: "enterprise" },
      { id: "PIPEDA", name: "PIPEDA", desc: "Canada Privacy", tier: "enterprise" },
      { id: "PDPA", name: "PDPA", desc: "Singapore Privacy", tier: "enterprise" },
      { id: "POPIA", name: "POPIA", desc: "South Africa Privacy", tier: "enterprise" },
    ]
  },
  cloud: {
    name: "Cloud Security",
    icon: Cloud,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    borderColor: "border-cyan-500/30",
    frameworks: [
      { id: "CSA_STAR", name: "CSA STAR", desc: "Cloud Security Assurance", tier: "growth" },
      { id: "CSA_CCM", name: "CSA CCM v4", desc: "Cloud Controls Matrix", tier: "enterprise" },
    ]
  },
  financial: {
    name: "Financial Services",
    icon: Building2,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    frameworks: [
      { id: "PCI_DSS", name: "PCI-DSS v4.0", desc: "Payment Card Security", tier: "growth" },
      { id: "GLBA", name: "GLBA", desc: "Financial Privacy", tier: "enterprise" },
      { id: "FFIEC", name: "FFIEC", desc: "Financial IT Guidelines", tier: "enterprise" },
      { id: "BASEL_III", name: "Basel III", desc: "Banking Regulations", tier: "enterprise" },
      { id: "MAS_TRM", name: "MAS TRM", desc: "Singapore Tech Risk", tier: "enterprise" },
      { id: "SWIFT_CSP", name: "SWIFT CSP", desc: "Banking Network Security", tier: "enterprise" },
    ]
  },
  application: {
    name: "Application Security",
    icon: Code,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
    frameworks: [
      { id: "OWASP_TOP_10", name: "OWASP Top 10", desc: "Web App Security Risks", tier: "free" },
      { id: "OWASP_ASVS", name: "OWASP ASVS", desc: "App Security Verification", tier: "growth" },
      { id: "OWASP_SAMM", name: "OWASP SAMM", desc: "Software Assurance Model", tier: "growth" },
      { id: "SANS_TOP_25", name: "SANS Top 25", desc: "Software Weaknesses", tier: "growth" },
    ]
  },
  industry: {
    name: "Industry-Specific",
    icon: Factory,
    color: "text-slate-400",
    bgColor: "bg-slate-500/10",
    borderColor: "border-slate-500/30",
    frameworks: [
      { id: "NERC_CIP", name: "NERC CIP", desc: "Energy Sector Security", tier: "enterprise" },
      { id: "IEC_62443", name: "IEC 62443", desc: "Industrial Control Security", tier: "enterprise" },
      { id: "TISAX", name: "TISAX", desc: "Automotive Security", tier: "enterprise" },
    ]
  },
  resilience: {
    name: "Risk & Resilience",
    icon: Activity,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    frameworks: [
      { id: "ISO_22301", name: "ISO 22301", desc: "Business Continuity", tier: "enterprise" },
      { id: "ISO_31000", name: "ISO 31000", desc: "Risk Management", tier: "enterprise" },
      { id: "COBIT", name: "COBIT 2019", desc: "IT Governance Framework", tier: "enterprise" },
      { id: "ITIL", name: "ITIL v4", desc: "IT Service Management", tier: "growth" },
    ]
  },
};

// Calculate total frameworks
export const TOTAL_FRAMEWORKS = Object.values(COMPLIANCE_FRAMEWORKS).reduce(
  (sum, cat) => sum + cat.frameworks.length, 0
);

/**
 * Landing Page - Compliance Hero Section
 * Shows framework categories with count and visual appeal
 */
export const ComplianceHeroSection = memo(() => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  
  // Convert to array for easier manipulation
  const categories = Object.entries(COMPLIANCE_FRAMEWORKS);
  
  // Smart layout: 9 categories arranged as 3-3-3 on desktop, 2-2-2-2-1 on tablet, 1 column on mobile
  // Or we can do 5-4 split for desktop (top row 5, bottom row 4 centered)
  
  return (
    <div className="relative">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: 'rgba(0,109,67,0.1)', border: '1px solid rgba(0,109,67,0.3)' }}>
          <Award className="w-4 h-4" style={{ color: '#006D43' }} />
          <span className="text-sm font-semibold" style={{ color: '#006D43' }}>Enterprise-Grade Compliance</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
          <span style={{ color: '#006D43' }}>{TOTAL_FRAMEWORKS}+</span> Industry Frameworks Supported
        </h2>
        <p className="max-w-2xl mx-auto text-sm md:text-base" style={{ color: '#94A3B8' }}>
          Every architecture is validated against major compliance standards across healthcare, 
          finance, government, and global privacy regulations.
        </p>
      </div>

      {/* Category Grid - Perfect 5x2 Symmetric Layout */}
      <div className="flex flex-col gap-3">
        {/* Top Row - 5 items */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {categories.slice(0, 5).map(([key, category]) => (
            <CategoryCard 
              key={key}
              categoryKey={key}
              category={category}
              isExpanded={expandedCategory === key}
              onToggle={() => setExpandedCategory(expandedCategory === key ? null : key)}
            />
          ))}
        </div>
        
        {/* Bottom Row - 5 items */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {categories.slice(5, 10).map(([key, category]) => (
            <CategoryCard 
              key={key}
              categoryKey={key}
              category={category}
              isExpanded={expandedCategory === key}
              onToggle={() => setExpandedCategory(expandedCategory === key ? null : key)}
            />
          ))}
        </div>
      </div>

      {/* Expanded Category Details */}
      {expandedCategory && (
        <div className="mt-4 p-4 rounded-xl animate-in fade-in slide-in-from-top-2 duration-300" style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(148,163,184,0.15)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {(() => {
                const Icon = COMPLIANCE_FRAMEWORKS[expandedCategory].icon;
                return <Icon className={cn("w-5 h-5", COMPLIANCE_FRAMEWORKS[expandedCategory].color)} />;
              })()}
              <span className="font-semibold text-white">
                {COMPLIANCE_FRAMEWORKS[expandedCategory].name} Frameworks
              </span>
            </div>
            <button 
              onClick={() => setExpandedCategory(null)}
              className="p-1 hover:text-white" style={{ color: '#94A3B8' }}
            >
              <ChevronUp className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {COMPLIANCE_FRAMEWORKS[expandedCategory].frameworks.map(fw => (
              <div 
                key={fw.id}
                className="p-3 rounded-lg transition-colors"
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(148,163,184,0.1)' }}
              >
                <div className="font-medium text-white text-sm">{fw.name}</div>
                <div className="text-xs mt-1" style={{ color: '#64748B' }}>{fw.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Stats */}
      <div className="mt-8 flex flex-wrap justify-center gap-4 md:gap-6">
        <div className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
          <Globe className="w-4 h-4" />
          <span className="text-xs md:text-sm">Global Coverage: USA, EU, APAC, LATAM</span>
        </div>
        <div className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
          <Zap className="w-4 h-4" style={{ color: '#006D43' }} />
          <span className="text-xs md:text-sm">Real-time Validation</span>
        </div>
        <div className="flex items-center gap-2" style={{ color: '#94A3B8' }}>
          <FileCheck className="w-4 h-4" style={{ color: '#34D399' }} />
          <span className="text-xs md:text-sm">Export Compliance Reports</span>
        </div>
      </div>
    </div>
  );
});

ComplianceHeroSection.displayName = 'ComplianceHeroSection';

/**
 * Individual Category Card Component
 */
const CategoryCard = memo(({ categoryKey, category, isExpanded, onToggle }) => {
  const Icon = category.icon;
  
  return (
    <div
      className="relative group cursor-pointer transition-all duration-300"
      onClick={onToggle}
    >
      <div className={cn(
        "p-4 rounded-xl border transition-all duration-300 h-full",
        category.bgColor,
        category.borderColor,
        "hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20",
        isExpanded && "ring-2 ring-[#006D43] ring-offset-2 ring-offset-[#101C2B]"
      )}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={cn("w-5 h-5 flex-shrink-0", category.color)} />
            <span className="font-semibold text-white text-xs sm:text-sm leading-tight">{category.name}</span>
          </div>
          <Badge variant="secondary" className="bg-white/10 text-white text-[10px] sm:text-xs ml-1 flex-shrink-0">
            {category.frameworks.length}
          </Badge>
        </div>
        
        {/* Framework preview badges */}
        <div className="flex flex-wrap gap-1 mt-2">
          {category.frameworks.slice(0, 2).map(fw => (
            <span key={fw.id} className="text-[9px] sm:text-[10px] px-1.5 py-0.5 bg-black/30 rounded text-zinc-300 truncate max-w-[80px]">
              {fw.name}
            </span>
          ))}
          {category.frameworks.length > 2 && (
            <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 text-zinc-500">
              +{category.frameworks.length - 2}
            </span>
          )}
        </div>
        
        {/* Expand indicator */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronDown className={cn(
            "w-4 h-4 text-zinc-500 transition-transform",
            isExpanded && "rotate-180"
          )} />
        </div>
      </div>
    </div>
  );
});

CategoryCard.displayName = 'CategoryCard';


/**
 * Phase 2 Audit - Compliance Coverage Card
 * Shows pass/fail status for each framework
 */
export const ComplianceCoverageCard = memo(({ 
  selectedFrameworks = [], 
  validatedFrameworks = [],
  complianceScore = 0 
}) => {
  const [activeTab, setActiveTab] = useState("all");
  
  // Get all frameworks that apply based on selection
  const applicableFrameworks = selectedFrameworks.length > 0 
    ? Object.values(COMPLIANCE_FRAMEWORKS).flatMap(cat => 
        cat.frameworks.filter(fw => selectedFrameworks.includes(fw.name))
      )
    : Object.values(COMPLIANCE_FRAMEWORKS).flatMap(cat => cat.frameworks);
  
  // validatedFrameworks from backend contains framework NAMES (not IDs)
  // So we compare with fw.name to properly match
  const passed = applicableFrameworks.filter(fw => validatedFrameworks.includes(fw.name));
  const failed = applicableFrameworks.filter(fw => !validatedFrameworks.includes(fw.name));
  
  const passedCount = passed.length;
  const totalCount = applicableFrameworks.length;
  const coveragePercent = totalCount > 0 ? Math.round((passedCount / totalCount) * 100) : 0;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#00f0ff]" />
            Compliance Coverage
          </CardTitle>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{coveragePercent}%</div>
              <div className="text-xs text-zinc-500">{passedCount}/{totalCount} frameworks</div>
            </div>
            {/* Circular Progress */}
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 transform -rotate-90">
                <circle cx="24" cy="24" r="20" fill="none" stroke="#27272a" strokeWidth="4" />
                <circle 
                  cx="24" cy="24" r="20" fill="none" 
                  stroke={coveragePercent >= 80 ? "#22c55e" : coveragePercent >= 50 ? "#f59e0b" : "#ef4444"}
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${coveragePercent * 1.256} 126`}
                />
              </svg>
              <CheckCircle2 className={cn(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5",
                coveragePercent >= 80 ? "text-green-500" : coveragePercent >= 50 ? "text-amber-500" : "text-red-500"
              )} />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800 border border-zinc-700 mb-4">
            <TabsTrigger value="all" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
              All ({totalCount})
            </TabsTrigger>
            <TabsTrigger value="passed" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
              Passed ({passedCount})
            </TabsTrigger>
            <TabsTrigger value="failed" className="data-[state=active]:bg-red-500 data-[state=active]:text-black">
              Action Needed ({failed.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-2">
              {applicableFrameworks.map(fw => {
                const isPassed = validatedFrameworks.includes(fw.name);
                return (
                  <FrameworkBadge key={fw.id} framework={fw} status={isPassed ? 'pass' : 'fail'} />
                );
              })}
            </div>
          </TabsContent>
          
          <TabsContent value="passed" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-2">
              {passed.map(fw => (
                <FrameworkBadge key={fw.id} framework={fw} status="pass" />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="failed" className="mt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto pr-2">
              {failed.map(fw => (
                <FrameworkBadge key={fw.id} framework={fw} status="fail" />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
});

ComplianceCoverageCard.displayName = 'ComplianceCoverageCard';


/**
 * Individual Framework Badge with status
 */
const FrameworkBadge = memo(({ framework, status = 'pending' }) => {
  const statusConfig = {
    pass: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    fail: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    warning: { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    pending: { icon: AlertCircle, color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30' },
  };
  
  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div className={cn(
      "p-2 rounded-lg border transition-all hover:scale-[1.02]",
      config.bg, config.border
    )}>
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-white text-xs truncate">{framework.name}</div>
          <div className="text-[10px] text-zinc-500 truncate">{framework.desc}</div>
        </div>
        <StatusIcon className={cn("w-4 h-4 flex-shrink-0", config.color)} />
      </div>
    </div>
  );
});

FrameworkBadge.displayName = 'FrameworkBadge';


/**
 * Phase 1 Form - Compliance Selection with Categories
 */
export const ComplianceSelector = memo(({ 
  selectedFrameworks = [], 
  onSelectionChange,
  maxSelections = null 
}) => {
  const [expandedCategories, setExpandedCategories] = useState(['security', 'healthcare']);
  
  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => 
      prev.includes(categoryKey) 
        ? prev.filter(k => k !== categoryKey)
        : [...prev, categoryKey]
    );
  };
  
  const toggleFramework = (frameworkName) => {
    if (selectedFrameworks.includes(frameworkName)) {
      onSelectionChange(selectedFrameworks.filter(f => f !== frameworkName));
    } else {
      if (maxSelections && selectedFrameworks.length >= maxSelections) {
        return; // Don't add if at max
      }
      onSelectionChange([...selectedFrameworks, frameworkName]);
    }
  };
  
  const selectPreset = (presetName) => {
    const presets = {
      'Healthcare Bundle': ['HIPAA', 'HITRUST CSF', 'HITECH'],
      'Financial Bundle': ['PCI-DSS v4.0', 'SOC 2 Type II', 'SOX', 'GLBA'],
      'Government Bundle': ['FedRAMP', 'NIST 800-53', 'FISMA'],
      'Privacy Bundle': ['GDPR', 'CCPA/CPRA', 'ISO 27001'],
      'Startup Essentials': ['SOC 2 Type II', 'GDPR', 'OWASP Top 10'],
    };
    onSelectionChange(presets[presetName] || []);
  };

  return (
    <div className="space-y-4">
      {/* Quick Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-xs text-zinc-500 self-center mr-2">Quick Select:</span>
        {['Healthcare Bundle', 'Financial Bundle', 'Government Bundle', 'Privacy Bundle', 'Startup Essentials'].map(preset => (
          <button
            key={preset}
            onClick={() => selectPreset(preset)}
            className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-[#00f0ff]/50 rounded-full text-zinc-300 hover:text-white transition-all"
          >
            {preset}
          </button>
        ))}
      </div>

      {/* Selected Count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-400">
          Selected: <span className="text-white font-semibold">{selectedFrameworks.length}</span> frameworks
        </span>
        {selectedFrameworks.length > 0 && (
          <button 
            onClick={() => onSelectionChange([])}
            className="text-xs text-zinc-500 hover:text-white"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Category Accordions */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {Object.entries(COMPLIANCE_FRAMEWORKS).map(([key, category]) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.includes(key);
          const selectedInCategory = category.frameworks.filter(fw => 
            selectedFrameworks.includes(fw.name)
          ).length;
          
          return (
            <div key={key} className={cn(
              "border rounded-lg overflow-hidden transition-all",
              category.borderColor,
              category.bgColor
            )}>
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(key)}
                className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Icon className={cn("w-4 h-4", category.color)} />
                  <span className="font-medium text-white text-sm">{category.name}</span>
                  {selectedInCategory > 0 && (
                    <Badge className="bg-[#00f0ff] text-black text-[10px] px-1.5">
                      {selectedInCategory}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">{category.frameworks.length} frameworks</span>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </button>
              
              {/* Framework List */}
              {isExpanded && (
                <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {category.frameworks.map(fw => {
                    const isSelected = selectedFrameworks.includes(fw.name);
                    return (
                      <button
                        key={fw.id}
                        onClick={() => toggleFramework(fw.name)}
                        className={cn(
                          "p-2 rounded-lg text-left transition-all border",
                          isSelected 
                            ? "bg-[#00f0ff]/20 border-[#00f0ff]/50" 
                            : "bg-black/20 border-transparent hover:border-zinc-600"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "font-medium text-sm",
                            isSelected ? "text-[#00f0ff]" : "text-white"
                          )}>
                            {fw.name}
                          </span>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-[#00f0ff]" />}
                        </div>
                        <div className="text-[10px] text-zinc-500">{fw.desc}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

ComplianceSelector.displayName = 'ComplianceSelector';


/**
 * Simple Framework Badge Row (for compact displays)
 */
export const ComplianceFrameworkBadges = memo(({ 
  frameworks = ["NIST 800-53", "ISO 27001", "PCI-DSS", "HIPAA", "FedRAMP", "SOC 2"],
  showCount = true,
  maxVisible = 6
}) => {
  const visibleFrameworks = frameworks.slice(0, maxVisible);
  const hiddenCount = frameworks.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-2 items-center">
      {showCount && (
        <span className="text-sm text-[#00f0ff] font-semibold mr-2">
          {TOTAL_FRAMEWORKS}+ Frameworks
        </span>
      )}
      {visibleFrameworks.map((framework, idx) => (
        <span 
          key={idx}
          className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs font-mono text-zinc-300 hover:border-[#00f0ff]/50 hover:text-[#00f0ff] transition-colors cursor-default"
        >
          {framework}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span className="text-xs text-zinc-500">
          +{hiddenCount} more
        </span>
      )}
    </div>
  );
});

ComplianceFrameworkBadges.displayName = 'ComplianceFrameworkBadges';


export default {
  ComplianceHeroSection,
  ComplianceCoverageCard,
  ComplianceSelector,
  ComplianceFrameworkBadges,
  COMPLIANCE_FRAMEWORKS,
  TOTAL_FRAMEWORKS,
};
